/**
 * Placement and scaling maths.
 *
 * STL files carry no units. We take them to be millimetres, which is the near-universal
 * convention for 3D printing, and WebXR works in metres — so a factor of 1/1000 is what
 * makes a 10 mm feature in the file measure 1 cm to someone wearing the headset.
 *
 * Everything here is a pure function over plain numbers, so it can be tested without a
 * renderer and reimplemented in another language without translation guesswork.
 */

import type { Bounds, Vec3 } from './types.js';

export const MM_TO_METRES = 0.001;

/** How much empty space to leave around the model when framing the desktop camera. */
const FRAMING_MARGIN = 1.15;

/** Clearance in metres between the viewer and the nearest face of the model in VR. */
const VR_CLEARANCE_M = 0.5;

/** Models no taller than this float at chest height; taller ones stand on the floor. */
const VR_FLOATING_HEIGHT_LIMIT_M = 0.5;

/** A comfortable height to inspect a small object at, roughly chest to chin. */
const VR_CHEST_HEIGHT_M = 1.2;

export function boundsSize(bounds: Bounds): Vec3 {
  const [minX, minY, minZ] = bounds.min;
  const [maxX, maxY, maxZ] = bounds.max;
  return [maxX - minX, maxY - minY, maxZ - minZ];
}

export function boundsCentre(bounds: Bounds): Vec3 {
  const [minX, minY, minZ] = bounds.min;
  const [maxX, maxY, maxZ] = bounds.max;
  return [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2];
}

export function maxDimension(bounds: Bounds): number {
  const [width, height, depth] = boundsSize(bounds);
  return Math.max(width, height, depth);
}

/** Radius of the sphere enclosing the bounds, measured from their centre. */
export function boundingRadius(bounds: Bounds): number {
  const [width, height, depth] = boundsSize(bounds);
  return Math.hypot(width, height, depth) / 2;
}

/**
 * Translation to bake into the geometry so the model is centred horizontally and its
 * lowest point sits at y = 0 — it stands on its own base.
 *
 * That origin is what lets the same geometry serve both modes: on the desktop it gives a
 * stable pivot for orbiting, and in VR it means resting the model on the floor is just
 * `position.y = 0`.
 */
export function computeCenteringOffset(bounds: Bounds): Vec3 {
  const [centreX, , centreZ] = boundsCentre(bounds);
  const [, minY] = bounds.min;
  return [-centreX, -minY, -centreZ];
}

/**
 * Uniform scale that normalises the model's longest edge to `targetSize`, so a 1 mm part
 * and a 300 mm part both arrive on screen at a workable size.
 *
 * Degenerate bounds (a single point, or an empty model) scale by 1 rather than dividing
 * by zero.
 */
export function computeFitScale(bounds: Bounds, targetSize = 1): number {
  const largest = maxDimension(bounds);
  return largest > 0 ? targetSize / largest : 1;
}

/**
 * Distance at which a sphere of `radius` fits inside the camera frustum.
 *
 * Uses whichever of the vertical and horizontal fields of view is narrower, so tall thin
 * windows frame correctly rather than clipping the sides.
 */
export function computeCameraDistance(
  radius: number,
  verticalFovDegrees: number,
  aspect: number,
  margin = FRAMING_MARGIN,
): number {
  const verticalFov = (verticalFovDegrees * Math.PI) / 180;
  const horizontalFov =
    2 * Math.atan(Math.tan(verticalFov / 2) * Math.max(aspect, 0.0001));
  const limitingFov = Math.min(verticalFov, horizontalFov);

  // sin, not tan: we are fitting a sphere tangent to the frustum walls, not a flat plane.
  return (radius / Math.sin(limitingFov / 2)) * margin;
}

/**
 * Where a camera stands to give the three-quarter view: to the right, above, and in front.
 *
 * One viewpoint for the whole application, because it is answering the same question every
 * time it is used — what does this thing look like? Straight-on views are the trap here: a
 * flat plate seen edge-on is a line, and a box seen face-on is a square whatever is behind
 * it. Off all three axes at once, every model shows at least three of its faces.
 */
const VIEW_AZIMUTH = Math.PI * 0.25;
const VIEW_ELEVATION = Math.PI * 0.16;

/**
 * How far, and in which direction, to stand back from whatever is being looked at.
 *
 * An offset rather than a position, so the caller adds it to whichever point they mean to
 * look at — the middle of one model on a screen, or the middle of one being photographed
 * for the sidebar.
 */
export function orbitOffset(distance: number): Vec3 {
  return [
    Math.sin(VIEW_AZIMUTH) * Math.cos(VIEW_ELEVATION) * distance,
    Math.sin(VIEW_ELEVATION) * distance,
    Math.cos(VIEW_AZIMUTH) * Math.cos(VIEW_ELEVATION) * distance,
  ];
}

export interface VrPlacement {
  /** Uniform scale taking the model's millimetres to the headset's metres. */
  readonly scale: number;
  /** Where to put the model's origin, relative to where the viewer is standing. */
  readonly position: Vec3;
}

/**
 * Place the model in front of a standing viewer at true size.
 *
 * Small objects float at chest height, where you would hold something you were examining.
 * Anything substantial rests on the floor instead, because a floating wardrobe is
 * disconcerting and a wardrobe standing on the floor is exactly the thing you wanted to
 * judge the size of.
 *
 * `sizeMm` is the model's extent, not its bounds, because the geometry has already been
 * recentred by `computeCenteringOffset` by the time this is applied.
 */
export function computeVrPlacement(sizeMm: Vec3): VrPlacement {
  return computeRoomPlacement(sizeMm, []);
}

/** Ground a model already occupies, seen from above. */
export interface Occupancy {
  /** Where its origin sits, in metres. */
  readonly position: Vec3;
  /** Radius of the circle its footprint fits inside, in metres. */
  readonly radius: number;
}

/**
 * Radius of the circle the model's footprint fits inside, in metres.
 *
 * Its footprint rather than its whole extent: two models can stand next to each other
 * however tall they are, and taking height into account would space out a pair of
 * flagpoles as though they were barrels.
 */
export function footprintRadius(sizeMm: Vec3): number {
  const [widthMm, , depthMm] = sizeMm;
  return (Math.hypot(widthMm, depthMm) / 2) * MM_TO_METRES;
}

/** Gap left between two models standing next to each other, in metres. */
const ROOM_SPACING_M = 0.12;

/** How far round the arc each attempt steps, in radians. */
const ROOM_ANGLE_STEP = Math.PI / 15;

/**
 * How far round from straight ahead a model may be put.
 *
 * Comfortably short of a right angle, so everything placed is genuinely in front of the
 * viewer rather than beside or behind them: having asked for a model to be brought out,
 * they should see it appear without having to be told where to look. Wider rings take the
 * overflow instead.
 */
const ROOM_MAX_ANGLE = Math.PI * 0.47;

/** How much further out each successive ring sits, in metres. */
const ROOM_RING_STEP_M = 0.7;

const ROOM_MAX_RINGS = 4;

/**
 * Place a model in the room without standing it inside anything already there.
 *
 * Models are arranged on an arc at a comfortable arm's length, sweeping outwards from
 * straight ahead: the first one asked for lands where a single model has always landed,
 * and each one after it takes the nearest free spot to either side. Once the arc is full
 * the search moves to a wider one, so a roomful never fails to place a model — the worst
 * case is that it ends up further away than is convenient, which the user can fix by
 * picking it up, and which is better than a model that was asked for and never appeared.
 *
 * Occupancy is judged in plan view, ignoring height. Two models at different heights
 * could in principle share a footprint without touching, but "hovering inside the space
 * above another model" is not an arrangement anybody asks for, and the flat test is one
 * that can be reasoned about while wearing a headset.
 */
export function computeRoomPlacement(
  sizeMm: Vec3,
  occupied: readonly Occupancy[],
): VrPlacement {
  const [, heightMm, depthMm] = sizeMm;
  const height = heightMm * MM_TO_METRES;
  const depth = depthMm * MM_TO_METRES;

  // The model's origin is at the centre of its footprint, so half the depth is what sits
  // between the origin and the face nearest the viewer.
  const nearestDistance = VR_CLEARANCE_M + depth / 2;

  const floats = height <= VR_FLOATING_HEIGHT_LIMIT_M;
  const y = floats ? Math.max(VR_CHEST_HEIGHT_M - height / 2, 0) : 0;

  const radius = footprintRadius(sizeMm);

  for (let ring = 0; ring < ROOM_MAX_RINGS; ring += 1) {
    const distance = nearestDistance + ring * ROOM_RING_STEP_M;

    for (const angle of sweep()) {
      // Rotating about the upright axis, from straight ahead at -z. Positive angles go to
      // the viewer's right, which is only a matter of which side fills up first.
      const position: Vec3 = [Math.sin(angle) * distance, y, -Math.cos(angle) * distance];

      if (isClear(position, radius, occupied)) {
        return { scale: MM_TO_METRES, position };
      }
    }
  }

  // Every ring is full. Standing it straight ahead, overlapping, is at least somewhere
  // the user is looking, and it can be picked up and moved from there.
  return { scale: MM_TO_METRES, position: [0, y, -nearestDistance] };
}

/** Angles to try, from straight ahead outwards, alternating sides. */
function* sweep(): Generator<number> {
  yield 0;

  for (let step = 1; step * ROOM_ANGLE_STEP <= ROOM_MAX_ANGLE; step += 1) {
    yield step * ROOM_ANGLE_STEP;
    yield -step * ROOM_ANGLE_STEP;
  }
}

function isClear(
  position: Vec3,
  radius: number,
  occupied: readonly Occupancy[],
): boolean {
  const [x, , z] = position;

  return occupied.every((other) => {
    const [otherX, , otherZ] = other.position;
    const apart = Math.hypot(x - otherX, z - otherZ);
    return apart >= radius + other.radius + ROOM_SPACING_M;
  });
}
